import {
  formatLevelData,
  getAnimationType,
  mapState,
  sendMessage,
} from '@/utility';
import {
  upperFirst,
  isEmpty,
  filter,
} from 'lodash';
import types from './mutationTypes';
import { dataApi } from '../api';

export default {
  async loadData({ dispatch }, payload) {
    const response = await dispatch('apiHandler', { action: 'loadData', payload });
    return response;
  },
  async putData({ dispatch }, payload) {
    const response = await dispatch('apiHandler', { action: 'putData', payload });
    return response;
  },
  async createData({ dispatch }, payload) {
    const response = await dispatch('apiHandler', { action: 'createData', payload });
    return response;
  },
  async updateData({ dispatch }, payload) {
    const response = await dispatch('apiHandler', { action: 'updateData', payload });
    return response;
  },
  async apiHandler({ commit, dispatch }, data) {
    try {
      const response = await dataApi[data.action](data.payload);
      if (data.payload.name) {
        commit(types[`SET_${data.payload.name.toUpperCase()}`], response);
      }
      return response;
    } catch (error) {
      if (data.payload.required) {
        dispatch('navigate', { to: 'error' });
        commit(types.TOGGLE_LOADER, false);
      }
      return { error };
    }
  },
  async getProfile({ commit, getters, dispatch }) {
    const response = await dispatch('loadData', {
      path: `profiles/${getters.playerId}?widget=${getters.widget}`,
      required: !getters.profileData,
    });
    if (response.data) {
      if (isEmpty(response.data)) {
        commit(types.SET_NEW_PLAYER, true);
      }
      const formattedLevelData = formatLevelData(response);
      commit(types.SET_PROFILE, formattedLevelData);
      commit(types.UPDATE_STARS_PROGRESS_BAR, response?.data?.level?.currentLevelPct);
      dispatch('updateProgressBar', response?.data?.level?.currentLevelPct);
    }
  },
  async getOtherPlayerProfile({ dispatch, commit, getters }) {
    const response = await dispatch('loadData', { path: `profiles/${getters.otherPlayerId}?widget=${getters.widget}` });
    if (response.data) {
      commit(types.SET_OTHER_PLAYER_PROFILE, formatLevelData(response));
      setTimeout(() => {
        dispatch('toggleLoader', false);
      }, 200);
    }
    if (response.error) {
      setTimeout(() => {
        dispatch('closeOtherPlayerProfile');
        dispatch('toggleLoader', false);
        commit(types.SET_ERROR_DETAILS, { selfClosing: true });
      }, 500);
    }
  },
  async getFeed({ dispatch, getters }) {
    await dispatch('loadData', {
      path: `feed/${getters.tenantId}?size=${getters.boostState.feedSize}&widget=${getters.widget}&feedType=${getters.channel}`,
      name: 'feed',
      required: !getters.feedData,
    });
  },
  async maskDisplayName({ commit, dispatch, getters }) {
    const response = await dispatch('putData', {
      path: `profiles/${getters.playerId}/settings/is-masked`,
      data: { isMasked: !getters.boostState.isMasked },
    });
    if (response.success) {
      commit(types.UPDATE_BOOST_STATE, { prop: 'isMasked', value: !getters.boostState.isMasked });
      dispatch('getProfile');
    }
    if (response.error) commit(types.SET_ERROR_DETAILS);
  },
  async setCustomDisplayName({ dispatch, getters, state }, payload) {
    const response = await dispatch('putData', {
      path: `profiles/${getters.playerId}/settings/display-name`,
      data: { customDisplayName: payload },
    });

    if (response.error) {
      const errorKey = response.error.response.data.code === 'DISPLAY_NAME_NOT_UNIQUE' ? 'NameTaken' : 'GlobalError';
      return {
        error: true,
        text: state.translations[`nicknameValidationMessage${errorKey}`],
      };
    }

    return {
      success: true,
      text: state.translations.nicknameValidationMessageSuccess,
    };
  },
  async getTopWins({ dispatch, getters }) {
    await dispatch('loadData', {
      path: `feed/${getters.tenantId}/top-wins?widget=${getters.widget}&size=${getters.boostState.feedSize}&feedType=${getters.channel}`,
      name: 'topWins',
      required: !getters.topWinsData,
    });
  },
  async getSettings({ dispatch, getters }) {
    const response = await dispatch('loadData', { path: `feed/${getters.tenantId}/settings?widget=${getters.widget}&profileType=${getters.channel}` });

    const { navigation, boostState } = mapState(response, getters.config);
    if (response.data?.tenant?.id) {
      dispatch('updateTenantId', response.data.tenant.id);
    }
    dispatch('saveSettings', { settings: response, navigation, boostState });
  },
  updateTenantId({ commit }, tenantId) {
    commit(types.UPDATE_TENANT_ID, tenantId);
  },
  saveSettings({ commit }, payload) {
    commit(types.SET_SETTINGS, payload.settings);
    commit(types.SET_NAVIGATION, payload.navigation);
    commit(types.SET_BOOST_STATE, payload.boostState);
  },
  async getTournaments({ dispatch, getters }) {
    await dispatch('loadData', {
      path: 'tournaments/active',
      name: 'active_tournaments',
      required: !getters.activeTournaments.length,
    });
    await dispatch('loadData', {
      path: 'tournaments/finished',
      name: 'finished_tournaments',
      required: !getters.finishedTournaments,
    });
    if (getters.subscribedToTournaments) {
      dispatch('streamTournaments');
    }
  },
  async getQuests({ dispatch, getters }) {
    await dispatch('loadData', {
      path: `quests/active/${getters?.profileData?.id}`,
      name: 'active_quests',
    });
    if (getters.activeQuests.length) {
      setTimeout(() => {
        sendMessage({
          action: 'showActiveQuests',
          recipient: 'badge',
          value: JSON.parse(JSON.stringify(getters.activeQuests)),
        });
      }, 2000);
    }
  },
  async streamTournaments({ dispatch, getters }) {
    let leaderboard = {};
    let activeTournaments = [];
    let parsedActiveTournaments = [];
    let playerTournaments = [];
    const tournamentsInProgress = filter(getters.activeTournaments,
      (tournament) => tournament.status === 'InProgress');

    activeTournaments = Promise.all(tournamentsInProgress.map(async (tournament) => {
      leaderboard = await dispatch('getPlayerLeaderBoard', tournament);
      if (leaderboard.position) {
        Object.assign(tournament, leaderboard);
      }
      return tournament;
    }));

    activeTournaments.then((tournament) => {
      playerTournaments = filter(tournament, (tour) => tour.position);
      parsedActiveTournaments = JSON.parse(JSON.stringify(playerTournaments));
      sendMessage({
        action: 'updateActiveTournaments',
        recipient: 'badge',
        value: parsedActiveTournaments,
      });
    });
  },
  async getTournamentDetails({ dispatch, commit, getters }, payload) {
    dispatch('toggleLoader', true);
    try {
      const tournamentDetails = await dispatch('loadData', { path: `tournaments/${payload.tournamentId}/details` });
      commit(types.SET_TOURNAMENT_DETAILS, tournamentDetails);

      await dispatch('getTournamentLeaderboard', { page: 1 });
      dispatch('navigate', {
        to: 'tournamentDetails',
        action: 'closeTournamentDetails',
        title: getters.tournamentDetails.displayName,
        animation: 'fade-left',
      });

      setTimeout(() => {
        dispatch('toggleLoader', false);
      }, 200);
    } catch (error) {
      setTimeout(() => {
        dispatch('toggleLoader', false);
        commit(types.SET_ERROR_DETAILS, { selfClosing: true });
      }, 500);
    }
  },
  async getTournamentLeaderboard({ dispatch, getters, commit }, payload) {
    const tournamentLeaderboard = await dispatch('loadData', { path: `tournaments/${getters.tournamentDetails.id}/leaderboard?page=${payload.page}&size=${getters.tournamentLeaderboardSize}` });
    commit(types.SET_TOURNAMENT_LEADERBOARD, tournamentLeaderboard);

    if (getters.playerId) {
      const playerLeaderboard = await dispatch('loadData', { path: `tournaments/${getters.tournamentDetails.id}/leaderboard/positions/${getters.playerId}` });
      commit(types.SET_PLAYER_LEADERBOARD, playerLeaderboard);
    }
  },
  async getPlayerLeaderBoard({ dispatch, getters }, payload) {
    const { id } = payload;
    let playerLeaderboard = {};
    const response = await dispatch('loadData', { path: `tournaments/${id}/leaderboard/positions/${getters.playerId}` });
    const tournamentPayload = JSON.parse(JSON.stringify(payload));
    playerLeaderboard = JSON.parse(JSON.stringify(response.data));
    if (playerLeaderboard && playerLeaderboard.position) {
      sendMessage({
        action: 'updatePlayerTournamentPosition',
        recipient: 'badge',
        value: {
          ...tournamentPayload,
          ...playerLeaderboard,
        },
      });
    }
    return playerLeaderboard;
  },
  async expandLeaderboard({ dispatch, commit, getters }, payload) {
    const leaderboard = await dispatch('loadData', { path: `tournaments/${getters.tournamentDetails.id}/leaderboard?size=${payload.page * getters.tournamentLeaderboardSize}` });
    commit(types.EXPAND_LEADERBOARD, { data: leaderboard.data, page: payload.page });
  },
  async refreshTournamentLeaderboards({ dispatch, commit, getters }) {
    const s = getters.tournamentLeaderboardSize;
    const numberOfPlayers = Math.ceil(getters.tournamentLeaderboard.data.length / s) * s || s;
    const tournamentLeaderboard = await dispatch('loadData', { path: `tournaments/${getters.tournamentDetails.id}/leaderboard?size=${numberOfPlayers}` });
    if (tournamentLeaderboard.data) {
      commit(types.UPDATE_TOURNAMENT_LEADERBOARD, tournamentLeaderboard);
    }

    if (getters.playerId) {
      const playerLeaderboard = await dispatch('loadData', { path: `tournaments/${getters.tournamentDetails.id}/leaderboard/positions/${getters.playerId}` });
      if (playerLeaderboard.data) {
        commit(types.SET_PLAYER_LEADERBOARD, playerLeaderboard);
      }
    }
  },
  async handleTournamentUpdate({ dispatch, commit, getters }, payload) {
    await dispatch('getTournaments');

    if (getters.navigation?.currentModule === 'TournamentDetails' && payload.tournamentId === getters.tournamentDetails?.id) {
      dispatch('toggleLoader', true);
      if (payload.newStatus === 'Canceled' || payload.newStatus === 'Stopped' || payload.visibilityEnded) {
        dispatch('closeTournamentDetails');
      } else {
        const tournamentDetails = await dispatch('loadData', { path: `tournaments/${payload.tournamentId}/details` });
        if (tournamentDetails.data) {
          commit(types.SET_TOURNAMENT_DETAILS, tournamentDetails);
        }
        await dispatch('refreshTournamentLeaderboards');
      }

      setTimeout(() => { dispatch('toggleLoader', false); }, 500);
    }
  },
  navigate({ commit, state }, payload) {
    const navigation = {
      animation: getAnimationType(state.navigation.modules, payload.to, state.navigation.currentModule),
    };

    if ('to' in payload) {
      navigation.currentModule = upperFirst(payload.to);
    }
    if ('action' in payload) {
      if (payload.action === '') {
        delete navigation.animation;
      }
      navigation.action = payload.action;
    }
    if ('title' in payload) {
      navigation.title = payload.title;
    }
    if ('animation' in payload) {
      navigation.animation = payload.animation;
    }
    commit(types.UPDATE_NAVIGATION, navigation);
  },
  updateBoostState({ commit }, payload) {
    commit(types.UPDATE_BOOST_STATE, payload);
  },
  lazyLoadRequests({ dispatch, getters }) {
    if (getters.navigation.tournaments) {
      dispatch('getTournaments');
    }
    if (getters.navigation.feed) {
      dispatch('getFeed');
      if (!getters.boostState.isRetailIntegration) {
        dispatch('getTopWins');

        // Refresh top wins every 5 minutes
        setInterval(() => {
          dispatch('getTopWins');
        }, 300_000);
      }
    }
  },
  updateProgressBar({ commit }, payload) {
    commit(types.UPDATE_PROGRESS_BAR, payload);
    sendMessage({
      action: 'updateProgressBar',
      recipient: 'badge',
      value: payload,
    });
  },
  setOtherPlayerId({ commit }, payload) {
    commit(types.SET_OTHER_PLAYER_ID, payload);
  },
  deleteOtherPlayerProfileData({ commit }) {
    commit(types.DELETE_OTHER_PLAYER_PROFILE_DATA);
  },
  viewOtherPlayerProfile({ dispatch, commit }, payload) {
    if (payload) {
      dispatch('toggleLoader', true);
      dispatch('setOtherPlayerId', payload);
      dispatch('navigate', { to: 'achievements', action: 'closeOtherPlayerProfile' });
      dispatch('getOtherPlayerProfile');
    } else {
      commit(types.SET_ERROR_DETAILS, { selfClosing: true });
    }
  },
  viewPlayerProfile({ dispatch }) {
    dispatch('toggleLoader', true);
    dispatch('navigate', { to: 'achievements', action: 'closePlayerProfile' });
    setTimeout(() => {
      dispatch('toggleLoader', false);
    }, 300);
  },
  closePlayerProfile({ dispatch, getters }) {
    setTimeout(() => {
      dispatch('navigate', { action: '' });
    }, 200);
    dispatch('navigate', { to: getters.navigation.modules[0] });
  },
  closeTournamentDetails({ dispatch, commit }) {
    setTimeout(() => {
      dispatch('navigate', { action: '', title: '' });
    }, 200);
    dispatch('navigate', { to: 'tournaments' });
    commit(types.CLEAR_TOURNAMENT_LEADERBOARD);
  },
  closeOtherPlayerProfile({ dispatch }) {
    setTimeout(() => {
      dispatch('navigate', { action: '' });
    }, 200);
    dispatch('setOtherPlayerId', '');
    dispatch('deleteOtherPlayerProfileData');
    dispatch('navigate', { to: 'feed' });
  },
  closeQuestPrizeDetails({ dispatch }) {
    setTimeout(() => {
      dispatch('navigate', { action: '' });
    }, 200);
    dispatch('navigate', { to: 'quests' });
  },
  toggleNewLevelPopup({ commit }, payload) {
    commit(types.TOGGLE_NEW_LEVEL_POPUP, payload);
  },
  toggleLoader({ commit }, payload) {
    commit(types.TOGGLE_LOADER, payload);
  },
  setErrorDetails({ commit }, payload) {
    commit(types.SET_ERROR_DETAILS, payload);
  },
  closeError({ commit }) {
    commit(types.CLOSE_ERROR);
  },
  setModulesVisibility({ commit }, payload) {
    commit(types.SET_MODULES_VISIBILITY, payload);
  },
  newTicket({ state, commit }, payload) {
    commit(types.POPULATE_STREAM, payload);

    if (state.feedData && state.ticketStream.length === 1) {
      commit(types.UPDATE_FEED);
    }
  },
  levelUpdate({
    state,
    commit,
    getters,
    dispatch,
  }, payload) {
    if (payload && state.newPlayer) {
      commit(types.SET_NEW_PLAYER, false);
    }
    commit(types.SET_NEW_LEVEL_NAME, payload.levelName?.currentLevelName);
    if (state.profileData?.level?.levelNumber < payload.levelNumber) {
      dispatch('updateProgressBar', [100, payload.currentLevelPct]);
      commit(types.UPDATE_STARS_PROGRESS_BAR, 100);
      setTimeout(() => {
        dispatch('updateProgressBar', payload.currentLevelPct);
        commit(types.UPDATE_STARS_PROGRESS_BAR, payload.currentLevelPct);
      }, 2200);
      if (getters.isAchievements && state.visibleModules && !state.otherPlayerId) {
        setTimeout(() => {
          commit(types.SET_NEW_LEVEL_ANIMATION_STATE, 'active');
        }, 2000);
        setTimeout(() => {
          commit(types.UPDATE_PROFILE, { data: payload, type: 'level' });
        }, 2200);
      } else {
        commit(types.SET_NEW_LEVEL_ANIMATION_STATE, 'ready');
        commit(types.UPDATE_PROFILE, { data: payload, type: 'level' });
      }
    } else {
      commit(types.UPDATE_STARS_PROGRESS_BAR, payload.currentLevelPct);
      dispatch('updateProgressBar', payload.currentLevelPct);
      commit(types.UPDATE_PROFILE, { data: payload, type: 'level' });
    }
  },
  statsUpdate({ commit }, payload) {
    commit(types.UPDATE_PROFILE, { data: payload, type: 'statsLatest' });
  },
  removeTicket({ commit }, payload) {
    commit(types.REMOVE_TICKET, payload);
  },
  setOffline({ commit }, payload) {
    commit(types.SET_OFFLINE, payload);
  },
  async refreshFeed({ commit, dispatch }) {
    commit(types.SET_OFFLINE, false);
    commit(types.TOGGLE_LOADER, true);
    commit(types.CLEAR_STREAM);
    commit(types.SET_FEED, '');
    await dispatch('getFeed');
    commit(types.TOGGLE_LOADER, false);
  },
  formatTournamentStatus({ commit, getters }) {
    commit(types.FORMAT_TOURNAMENT_STATUS, getters.tournamentDetails.status);
  },
  subscribeInstanceToTournaments({ commit }) {
    commit(types.SUBSCRIBE_INSTANCE_TO_TOURNAMENTS);
  },
  setActivePrize({ dispatch, commit }, payload) {
    commit(types.SET_ACTIVE_PRIZE, payload);
    dispatch('navigate', {
      to: 'questPrizeDetails',
      action: 'closeQuestPrizeDetails',
      animation: 'fade-left',
    });
  },
  newQuestReward({ commit }, payload) {
    commit(types.CREATE_NEW_QUEST_REWARD, payload);
  },
  updateQuestRewards({ commit }, payload) {
    commit(types.UPDATE_QUEST_REWARDS, payload);
  },
  getQuestRewards({ dispatch, getters }) {
    dispatch('loadData', {
      path: `quests/inventory/${getters.profileData.id}`,
      name: 'quest_rewards',
    });
  },
  handleQuestLevelUpdate({ commit, getters }, payload) {
    commit(types.UPDATE_QUEST_LEVEL, payload);
    sendMessage({
      action: 'updateQuestLevel',
      recipient: 'badge',
      value: JSON.stringify(getters.activeQuests, null, 2),
    });
  },
  getQuestsLastWinners({ dispatch }) {
    dispatch('loadData', {
      path: 'quests/last-winners',
      name: 'quests_last_winners',
    });
  },
  claimQuestReward({ dispatch, getters }, rewardId) {
    return dispatch('createData', {
      path: `quests/rewards/${rewardId}/actions/claim`,
      headers: {
        'x-nsft-b2b-token': getters.config.b2bToken || getters.config.authorizationToken,
      },
    });
  },
  getQuestReward({ dispatch, getters }, rewardId) {
    return dispatch('loadData', {
      path: `quests/rewards/${rewardId}`,
      headers: {
        'x-nsft-b2b-token': getters.config.b2bToken || getters.config.authorizationToken,
      },
    });
  },
  refreshQuests({ dispatch }) {
    dispatch('getQuests');
  },
  voidQuests({ commit }, payload) {
    commit(types.VOID_QUESTS, payload);
    sendMessage({
      action: 'voidQuests',
      recipient: 'badge',
    });
  },
  setActiveQuestCompleted({ commit, getters }, payload) {
    commit(types.UPDATE_QUESTS_STATE, payload);
    sendMessage({
      action: 'activeQuestCompleted',
      recipient: 'badge',
      value: JSON.stringify(getters.activeQuests, null, 2),
    });
  },
};
